import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { toast } from 'sonner';

interface ResetPasswordProps {
  setIsEditingUser: React.Dispatch<React.SetStateAction<boolean>>;
  isEditingUser: boolean;
  selectedUsername: string | null;
}

const ResetPasswordDialog = ({ setIsEditingUser, isEditingUser, selectedUsername }: ResetPasswordProps) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      setIsEditingUser(false);
    }
  };

  const resetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      if (!newPassword) {
        throw new Error('Please enter a new password');
      }

      if (!confirmPassword) {
        throw new Error('Please confirm the new password');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match!');
      }

      setIsLoading(true);

      const response = await fetch(`/api/v1/user/reset-password`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: selectedUsername, new_password: newPassword }),
      });

      const res = await response.json();

      if (!response.ok) throw new Error(res.message);

      toast.success(`Password for ${selectedUsername} has been successfully reset`);
      resetForm();
      setIsEditingUser(false);
    } catch (error: any) {
      toast.error(error.message || `An error occurred while resetting the password.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isEditingUser} onOpenChange={handleClose}>
      <AlertDialogContent className='sm:max-w-[400px]'>
        <AlertDialogHeader>
          <AlertDialogTitle className='text-xl font-bold'>Reset Password</AlertDialogTitle>
          <AlertDialogDescription>
            Reset password for user: <span className='font-semibold'>{selectedUsername}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={resetPassword}>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='newPassword'>New Password</Label>
              <Input
                id='newPassword'
                type='password'
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder='Enter new password'
                disabled={isLoading}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='confirmPassword'>Confirm Password</Label>
              <Input
                id='confirmPassword'
                type='password'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm new password'
                disabled={isLoading}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel type='button' disabled={isLoading}>
              Cancel
            </AlertDialogCancel>
            <Button type='submit' disabled={isLoading}>
              {isLoading ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ResetPasswordDialog;
